import { useCallback, useMemo } from "react";
import { toLocalDate, toDateOnlyISO } from "@/utils/datetime";
import SingleInput from "@/components/controls/SingleInput";
import { fromPercentInput, toPercentInput } from "@/utils/numbers";
import SingleSelect from "@/components/controls/SingleSelect";
import { MultiSelect } from "@/components/controls/MultiSelect";
import DateSelection from "@/components/controls/DateSelection";
import { createGetFieldKey, createGetStableFieldKey } from "@/utils/strings";
import { cn } from "@/utils/utils";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";
import { IconsMap } from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import IconContainer, { IconColor } from "@/components/ui/icon-container";
import { get, isUndefined, keys, map, mapValues } from "lodash";
import { getCreateNewLabel } from "@/utils/entityFieldHelpers";
import { AddButtonFooter } from "@/components/ui/add-button-footer";
import {
  createAddNewOptionHandler,
  CreateNewOptionContext
} from "@/utils/entityFieldsUtils/shared";

interface BaseEntityData {
  id: string;
  companyId?: string;
}

interface FieldConfig {
  type: "select" | "multiSelect" | "editableInput" | "nonEditable" | "date";
  icon?: string;
  iconColor?: IconColor;
  iconClassName?: string;
  placeholder?: string;
  defaultValue?: any;
  options?: any[];
  formatter?: (value: any) => string;
  renderOptionIcon?: (value: number | string) => JSX.Element;
  initialValue?: string | number;
  additionalProps?: Record<string, any>;
  entity?: EntityType;
  isMonetary?: boolean;
  isPercentage?: boolean;
  onSelect?: (value: any) => void;
  customComponent?: JSX.Element;
  searchTerm?: string;
  onSearch?: (newSearchVal: string) => void;
  isLoading?: boolean;
  showSearch?: boolean;
  popoverClassName?: string;
  showEmptyIcon?: boolean;
  disabled?: boolean;
  disabledTooltipText?: string;
  popOverFooter?: JSX.Element | ((searchTerm: string) => JSX.Element);
  // Entity field properties for popover footer functionality
  isEntityField?: boolean;
  displayType?: string;
  config?: any;
  onCreateNew?: () => void;
  createNewLabel?: string;
  customOnChange?: (value: string | number) => void;
  allowUsersToAdd?: boolean;
  fieldId?: string;
  // Date only: offers a clear control, which header dates otherwise withhold.
  clearable?: boolean;
}

export interface FieldsConfig {
  [field: string]: FieldConfig;
}

export interface TopFieldsContext extends CreateNewOptionContext {
  setView?: (view: "deals" | "companies" | "people") => void;
  onCompanySelect?: (companyId: string) => void;
  onProductSelect?: (productId: string) => void;
  // Modal functionality for creating new options
  showCreateOptionModal?: boolean;
  newOptionName?: string;
  currentFieldForNewOption?: { fieldId: string; fieldKey: string } | null;
  handleCreateNewOption?: (optionName: string) => Promise<void>;
  onUpdateField?: (
    field: string,
    newValue: string | number | boolean | string[] | any,
    additionalFields?: { [fieldName: string]: string | number }
  ) => void;
}

interface Props<T extends BaseEntityData> {
  entity?: T;
  fieldsConfig: FieldsConfig;
  onUpdateField: (field: string, newValue: string | number) => void;
  onUpdateStatus?: (newStatus: number) => void;
  className?: string;
  itemClassName?: string;
  context?: TopFieldsContext;
}

const TopFields = <T extends BaseEntityData>({
  entity,
  fieldsConfig,
  onUpdateField,
  className,
  itemClassName,
  context
}: Props<T>) => {
  const getFieldKey = useCallback(createGetFieldKey(entity), [entity?.id]);
  // For select/multiSelect: see createGetStableFieldKey — these are
  // popover-controlled and must not remount on every selection.
  const getStableFieldKey = useCallback(
    createGetStableFieldKey(entity),
    [entity?.id]
  );
  const { t } = useTranslation();

  const renderFieldComponent = (fieldName: string, config: FieldConfig) => {
    const component = (() => {
      switch (config.type) {
        case "nonEditable":
          return config.customComponent;
        case "select":
          // Add entity creation functionality for entity-based single select fields
          let singleSelectEntityProps = {};
          if (config.isEntityField && config.displayType && context) {
            singleSelectEntityProps = {
              onCreateNew: () => {
                if (config.displayType === "companies") {
                  context.setView?.("companies");
                } else if (config.displayType === "people") {
                  context.setView?.("people");
                }
              },
              createNewLabel: getCreateNewLabel(config.displayType, t),
              customOnChange:
                config.displayType === "companies"
                  ? context.onCompanySelect
                  : config.displayType === "products"
                    ? context.onProductSelect
                    : undefined
            };
          }

          // Add create new option functionality for non-entity single select fields
          const shouldShowCreateNew = config.allowUsersToAdd && context;
          const fieldKey = fieldName;
          const fieldId = config.fieldId || fieldName;

          let popOverFooter;
          if (shouldShowCreateNew) {
            popOverFooter = (searchTerm: string) => (
              <AddButtonFooter
                buttonText={t(
                  "settings.userManagement.addNewUserModal.createNewOption"
                )}
                onClick={createAddNewOptionHandler(
                  context,
                  fieldId,
                  fieldKey,
                  searchTerm
                )}
                variant="iconNoBg"
              />
            );
          } else if (config.isEntityField && config.displayType && context) {
            // Create popover footer for entity fields
            popOverFooter = () => (
              <AddButtonFooter
                buttonText={getCreateNewLabel(config.displayType!, t)}
                onClick={() => {
                  if (config.displayType === "companies") {
                    context.setView?.("companies");
                  } else if (config.displayType === "people") {
                    context.setView?.("people");
                  }
                }}
                variant="iconNoBg"
              />
            );
          } else if (config.popOverFooter) {
            // Support both function and JSX.Element formats
            popOverFooter =
              typeof config.popOverFooter === "function"
                ? config.popOverFooter
                : () => config.popOverFooter as JSX.Element;
          }

          return (
            <SingleSelect
              key={getStableFieldKey(fieldName as keyof T)}
              variant="topFields"
              fieldName={fieldName}
              options={config?.options || []}
              selectedValue={get(entity, fieldName, config.defaultValue)}
              onSelection={(newValue) =>
                config?.onSelect
                  ? config?.onSelect(newValue)
                  : onUpdateField(fieldName, newValue)
              }
              formatter={config.formatter}
              renderIcon={config.renderOptionIcon}
              entity={config.entity}
              initialSearchTerm={config?.searchTerm}
              onSearch={config?.onSearch}
              isLoading={config?.isLoading}
              showSearch={config?.showSearch || !!shouldShowCreateNew}
              popoverClassName={config?.popoverClassName}
              showEmptyIcon={config?.showEmptyIcon}
              disabled={config?.disabled}
              disabledTooltipText={config?.disabledTooltipText}
              popOverFooter={popOverFooter}
              mainContainerClassName="!shadow-none"
              {...singleSelectEntityProps}
              {...config.additionalProps}
            />
          );

        case "multiSelect": {
          const selectedValues = ((get(entity, fieldName) as
            | (string | number)[]
            | null
            | undefined) ?? []).map(String);
          return (
            <MultiSelect
              key={getStableFieldKey(fieldName as keyof T)}
              options={config?.options || []}
              selectedValues={selectedValues}
              onValueChange={(newValues) => config?.onSelect?.(newValues)}
              formatter={config.formatter}
              placeholder={config.placeholder}
              popoverClassName={config?.popoverClassName}
              showIconInChip
              matchTriggerWidth
              variant="topFields"
              {...config.additionalProps}
            />
          );
        }

        case "editableInput":
          const { key: additionalKey, ...restAdditionalProps } =
            config.additionalProps || {};
          const componentKey =
            additionalKey || getFieldKey(fieldName as keyof T);
          return (
            <SingleInput
              key={componentKey}
              variant="topFields"
              fieldName={fieldName}
              initialValue={
                config.isPercentage
                  ? (toPercentInput(config.initialValue)?.toString() ?? "")
                  : config.initialValue?.toString() || ""
              }
              onSave={(newValue: string) => {
                if (config.isPercentage) {
                  const stored = fromPercentInput(newValue);
                  onUpdateField(fieldName, stored === null ? newValue : stored);
                  return;
                }
                onUpdateField(
                  fieldName,
                  isNaN(Number(newValue)) ? newValue : Number(newValue)
                );
              }}
              textVariant="body"
              isMonetary={config.isMonetary}
              isPercentage={config.isPercentage}
              {...restAdditionalProps}
            />
          );

        case "date":
          return (
            <DateSelection
              key={getFieldKey(fieldName as keyof T)}
              variant="topFields"
              date={
                get(entity, fieldName)
                  ? toLocalDate(get(entity, fieldName))
                  : null
              }
              onDateSelection={(newDate: Date) =>
                onUpdateField(fieldName, toDateOnlyISO(newDate))
              }
              placeholder={config.placeholder}
              onClear={
                config.clearable
                  ? () => onUpdateField(fieldName, "")
                  : undefined
              }
            />
          );

        default:
          return null;
      }
    })();

    if (config.customComponent) {
      return component;
    }
    const wrappedComponent = (
      <div className="inline-block group">{component}</div>
    );
    // A disabled field shows its own tooltip; a label tooltip would stack on it
    if (config.disabled && config.disabledTooltipText) {
      return wrappedComponent;
    }
    return (
      <Tooltip>
        <TooltipTrigger asChild>{wrappedComponent}</TooltipTrigger>
        <TooltipContent>{t(`topFields.${fieldName}`)}</TooltipContent>
      </Tooltip>
    );
  };

  const topBarFields = useMemo(
    () =>
      mapValues(fieldsConfig, (config, fieldName) => ({
        component: renderFieldComponent(fieldName, config),
        icon: config.icon || "",
        iconColor: config.iconColor || "secondary",
        iconClassName: config.iconClassName
      })),
    [entity, fieldsConfig, onUpdateField, context]
  );

  return (
    !isUndefined(entity) && (
      <div className={cn("w-full flex px-4", className)}>
        {map(keys(topBarFields), (fieldName) => (
          <div
            key={fieldName}
            className={cn(
              "flex py-[2px] px-1 items-center gap-0 [&:not(:last-child)]:after:content-[''] [&:not(:last-child)]:after:h-4 [&:not(:last-child)]:after:w-[1px] [&:not(:last-child)]:after:bg-border-border2 [&:not(:last-child)]:after:mx-1",
              itemClassName
            )}
          >
            {IconsMap?.[topBarFields[fieldName].icon] && (
              <IconContainer
                size="sm"
                color={topBarFields[fieldName].iconColor}
                className={topBarFields[fieldName].iconClassName}
              >
                {IconsMap[topBarFields[fieldName].icon]}
              </IconContainer>
            )}
            {topBarFields[fieldName].component}
          </div>
        ))}
      </div>
    )
  );
};

export default TopFields;
